import React, { useMemo, useState } from 'react';
import { DataBundle, LineageNode } from '../types';
import { Box } from '@mui/material';

const btn: React.CSSProperties = {
  border: '1px solid #d1d5db',
  background: '#fff',
  borderRadius: 8,
  padding: '4px 8px',
  height: 28,
  cursor: 'pointer',
  fontSize: 12,
};

function queryAssets(bundle: DataBundle, q: string) {
  const query = q.trim().toLowerCase();
  if (!query) return bundle.assets || [];

  const hasToken = query.includes(':');

  if (hasToken) {
    const tokens = q.split(/\s+/).filter(Boolean);
    const params: Record<string, string[]> = {};
    for (const t of tokens) {
      const [k, v] = t.split(':');
      if (!v) continue;
      const key = k.toLowerCase();
      const val = v.toLowerCase();
      if (!params[key]) {
        params[key] = [];
      }
      params[key].push(val);
    }
    const match = (a: any, key: string, arr: string[]) => {
      const val = (
        (a[key.charAt(0).toUpperCase() + key.slice(1)] || a[key] || '') + ''
      ).toLowerCase();
      return arr.some((x) => val.includes(x));
    };
    const typeMatch = (a: any, arr: string[]) =>
      arr.some((x) => (a.AssetType || '').toLowerCase().includes(x));
    
    return (bundle.assets || []).filter((a) => {
      if (params['type'] && !typeMatch(a, params['type'])) return false;
      if (params['stage'] && !match(a, 'Stage', params['stage'])) return false;
      if (params['domain'] && !match(a, 'Domain', params['domain'])) return false;
      if (params['sector'] && !match(a, 'Sector', params['sector'])) return false;
      if (params['program'] && !match(a, 'Program', params['program'])) return false;
      if (params['name'] && !(a.Name || '').toLowerCase().includes(params['name'][0])) return false;
      if (params['id'] && !(a.AssetId || '').toLowerCase().includes(params['id'][0])) return false;
      return true;
    });
  }

  return (bundle.assets || []).filter((a) => {
    const name = (a.Name || '').toLowerCase();
    const assetId = (a.AssetId || '').toLowerCase();
    const type = (a.AssetType || '').toLowerCase();
    const domain = (a.Domain || '').toLowerCase();
    const sector = (a.Sector || '').toLowerCase();
    const program = (a.Program || '').toLowerCase();
    const stage = (a.Stage || '').toLowerCase();
    const subtype = (a.Subtype || '').toLowerCase();

    return (
      name.includes(query) ||
      assetId.includes(query) ||
      type.includes(query) ||
      domain.includes(query) ||
      sector.includes(query) ||
      program.includes(query) ||
      stage.includes(query) ||
      subtype.includes(query)
    );
  });
}

function NodeDetailView({
  node,
  businessImpact,
  dependents,
}: {
  node: any;
  businessImpact: string | null;
  dependents: any;
}) {
  return (
    <div style={{ marginBottom: 32, paddingBottom: 24, borderBottom: '1px solid #e5e7eb' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 8px' }}>
        <div>
          <div style={{ color: '#9ca3af', fontSize: 11, marginBottom: 2 }}>Name</div>
          <div style={{ color: '#111827', fontWeight: 600 }}>
            {node.data?.Name || node.label}
          </div>
        </div>
        <div>
          <div style={{ color: '#9ca3af', fontSize: 11, marginBottom: 2 }}>Type</div>
          <div style={{ color: '#111827', fontWeight: 600 }}>
            {node.group ? node.group.charAt(0).toUpperCase() + node.group.slice(1) : '-'}
          </div>
        </div>
        <div>
          <div style={{ color: '#9ca3af', fontSize: 11, marginBottom: 2 }}>Subtype</div>
          <div style={{ color: '#111827', fontWeight: 600 }}>
            {node.data?.Subtype || node.data?.AssetType || '-'}
          </div>
        </div>
        <div>
          <div style={{ color: '#9ca3af', fontSize: 11, marginBottom: 2 }}>Domain</div>
          <div style={{ color: '#111827', fontWeight: 600 }}>
            {node.data?.Domain || '-'}
          </div>
        </div>
        <div>
          <div style={{ color: '#9ca3af', fontSize: 11, marginBottom: 2 }}>Sector</div>
          <div style={{ color: '#111827', fontWeight: 600 }}>
            {node.data?.Sector || '-'}
          </div>
        </div>
        <div>
          <div style={{ color: '#9ca3af', fontSize: 11, marginBottom: 2 }}>Program</div>
          <div style={{ color: '#111827', fontWeight: 600 }}>
            {node.data?.Program || '-'}
          </div>
        </div>
      </div>

      <hr
        style={{ border: 'none', borderBottom: '1px solid #e5e7eb', margin: '20px 0' }}
      />

      <div style={{ color: '#003771', fontSize: 12, marginBottom: 6 }}>
        Business Impact
      </div>
      <div style={{ color: '#111827', whiteSpace: 'pre-wrap', marginBottom: 20 }}>
        {businessImpact || '---'}
      </div>

      <div style={{ color: '#003771', fontSize: 12, marginBottom: 6 }}>
        Dependent Pipelines
      </div>
      {dependents?.pipelines?.length ? (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}
        >
          {dependents.pipelines.map((p: any) => (
            <div key={p.id} style={{ color: '#111827', fontWeight: 600 }}>
              {p.label}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color: '#111827', marginBottom: 20 }}>---</div>
      )}

      <div style={{ color: '#003771', fontSize: 12, marginBottom: 6 }}>
        Dependent Notebooks
      </div>
      {dependents?.notebooks?.length ? (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}
        >
          {dependents.notebooks.map((p: any) => (
            <div key={p.id} style={{ color: '#111827', fontWeight: 600 }}>
              {p.label}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color: '#111827', marginBottom: 20 }}>---</div>
      )}

      <div style={{ color: '#003771', fontSize: 12, marginBottom: 6 }}>
        Dependent Applications
      </div>
      {dependents?.apps?.length ? (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}
        >
          {dependents.apps.map((p: any) => (
            <div key={p.id} style={{ color: '#111827', fontWeight: 600 }}>
              {p.label}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color: '#111827', marginBottom: 20 }}>---</div>
      )}
    </div>
  );
}

export default function Inspector({
  open,
  onClose,
  node,
  businessImpact,
  dependents,
  bundle,
  onSelectAssetId,
}: Readonly<{
  open: boolean;
  onClose: () => void;
  node: LineageNode | null;
  businessImpact: string | null;
  dependents: {
    pipelines: { id: string; label: string }[];
    notebooks: { id: string; label: string }[];
    apps: { id: string; label: string }[];
  } | null;
  bundle: DataBundle;
  onSelectAssetId: (id: string) => void;
}>) {
  const [tab, setTab] = useState<'details' | 'report'>('details');
  const [q, setQ] = useState('');
  const selectedAssetId = node?.data?.AssetId || node?.data?.id;

  React.useEffect(() => {
    if (open) {
      setTab(node ? 'details' : 'report');
    }
  }, [open, node]);

  const nodeReportBundle = useMemo(() => {
    if (!selectedAssetId) return bundle;

    const connectedIds = new Set<string>([String(selectedAssetId)]);

    const relatedEdges = (bundle.edges || []).filter((edge: any) => {
      const from = edge.From || edge.from || edge.source;
      const to = edge.To || edge.to || edge.target;

      const isRelated =
        String(from) === String(selectedAssetId) || String(to) === String(selectedAssetId);

      if (isRelated) {
        if (from) connectedIds.add(String(from));
        if (to) connectedIds.add(String(to));
      }

      return isRelated;
    });

    const relatedAssets = (bundle.assets || []).filter((asset: any) => {
      const assetId = asset.AssetId || asset.id;
      return connectedIds.has(String(assetId));
    });

    const fallbackAsset = node?.data
      ? [{ ...node.data, AssetId: node.data.AssetId || node.data.id }]
      : [];

    return {
      ...bundle,
      assets: relatedAssets.length ? relatedAssets : fallbackAsset,
      edges: relatedEdges,
      impacts: (bundle.impacts || []).filter((impact: any) => {
        const assetId = impact.AssetId || impact.id;
        return connectedIds.has(String(assetId));
      }),
      dqRules: bundle.dqRules || [],
      recons: bundle.recons || [],
      runs: bundle.runs || [],
    };
  }, [bundle, node, selectedAssetId]);

  const results = useMemo(() => {
    const targetBundle = node ? nodeReportBundle : bundle;
    return q ? queryAssets(targetBundle, q) : [];
  }, [bundle, nodeReportBundle, node, q]);

  const reportResults = useMemo(() => {
    return q ? results : nodeReportBundle.assets || [];
  }, [nodeReportBundle.assets, q, results]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#FFFFFF',
      }}
    >
      <Box sx={{ px: 3, pt: 3 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #003771',
          }}
        >
          {node ? (
            <div style={{ display: 'flex', gap: 24, marginBottom: -1, position: 'relative', zIndex: 1 }}>
              <button
                type="button"
                onClick={() => setTab('details')}
                style={{
                  cursor: 'pointer',
                  padding: 0,
                  paddingBottom: 8,
                  color: tab === 'details' ? '#00366E' : '#39ACE7',
                  fontWeight: tab === 'details' ? 600 : 400,
                  border: 'none',
                  borderBottom: tab === 'details' ? '5px solid #003771' : '5px solid transparent',
                  borderRadius: tab === 'details' ? '3px 3px 0 0' : 0,
                  background: 'transparent',
                  fontSize: 18,
                  fontFamily: 'inherit',
                }}
              >
                Details
              </button>

              <button
                type="button"
                onClick={() => setTab('report')}
                style={{
                  cursor: 'pointer',
                  padding: 0,
                  paddingBottom: 8,
                  color: tab === 'report' ? '#00366E' : '#39ACE7',
                  fontWeight: tab === 'report' ? 600 : 400,
                  border: 'none',
                  borderBottom: tab === 'report' ? '5px solid #003771' : '5px solid transparent',
                  borderRadius: tab === 'report' ? '3px 3px 0 0' : 0,
                  background: 'transparent',
                  fontSize: 18,
                  fontFamily: 'inherit',
                }}
              >
                Report
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 24, marginBottom: -1, position: 'relative', zIndex: 1 }}>
              <div
                style={{
                  padding: 0,
                  paddingBottom: 8,
                  color: '#00366E',
                  fontWeight: 600,
                  border: 'none',
                  borderBottom: '5px solid #003771',
                  borderRadius: '3px 3px 0 0',
                  background: 'transparent',
                  fontSize: 18,
                  fontFamily: 'inherit',
                }}
              >
                Details
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            style={{
              cursor: 'pointer',
              padding: 0,
              paddingBottom: 8,
              color: '#003771',
              fontWeight: 600,
              fontSize: 18,
              border: 'none',
              background: 'transparent',
            }}
          >
            ✕
          </button>
        </div>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p:3 }}>
        {tab === 'details' && node ? (
          <div>
                <div>
                  <NodeDetailView node={node} businessImpact={businessImpact} dependents={dependents} />
                </div>
          </div>
          ) : (
            <div
              style={{
                paddingRight: 4,
              }}
            >
              {!node && reportResults.length === 0 ? (
                <div style={{ color: '#4b5563' }}>No data available to report.</div>
              ) : (
                <>
                  <div
                    style={{
                      color: '#003771',
                      fontSize: 11,
                      fontWeight: 700,
                      marginBottom: 6,
                    }}
                  >
                    Asset Inspector (Query)
                  </div>

                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="e.g. type: pipeline stage: gold sector: emea"
                    style={{
                      width: 'calc(100% - 16px)',
                      height: 28,
                      border: '1px solid #d1d5db',
                      borderRadius: 4,
                      padding: '0 8px',
                      fontSize: 10,
                      outline: 'none',
                      marginBottom: 8,
                    }}
                  />

                  <div
                    style={{
                      color: '#9ca3af',
                      fontSize: 10,
                      fontWeight: 600,
                      marginBottom: 8,
                    }}
                  >
                    {reportResults.length} Results
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {!node
                      ? reportResults.map((asset: any) => {
                          const fakeNode = {
                            data: asset,
                            group: asset.AssetType || asset.Type || 'nodes',
                            label: asset.Name || asset.AssetName || asset.AssetId || asset.id,
                          };
                          return (
                            <NodeDetailView
                              key={asset.AssetId || asset.id || Math.random()}
                              node={fakeNode}
                              businessImpact={null}
                              dependents={null}
                            />
                          );
                        })
                      : reportResults.map((asset: any) => {
                          const assetId = asset.AssetId || asset.id || '';
                          const title = asset.Name || asset.AssetName || assetId || 'Unknown Asset';
                          const type = asset.AssetType || asset.Type || 'Other';
                          const domain = asset.Domain || '-';
                          const sector = asset.Sector || '-';
                          const program = asset.Program || '-';

                          return (
                            <button
                              key={assetId || title}
                              type="button"
                              onClick={() => {
                                if (assetId) {
                                  onSelectAssetId(assetId);
                                  setTab('details');
                                }
                              }}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            border: 'none',
                            background: '#EFF6FF',
                            borderRadius: 4,
                            padding: '8px 10px',
                            cursor: 'pointer',
                          }}
                        >
                          <div
                            style={{
                              color: '#003771',
                              fontSize: 11,
                              fontWeight: 700,
                              marginBottom: 2,
                            }}
                          >
                            {title} ({type})
                          </div>

                          <div
                            style={{
                              color: '#111827',
                              fontSize: 10,
                              fontWeight: 600,
                              marginBottom: 2,
                            }}
                          >
                            {domain}/{sector}/{program}
                          </div>

                          <div
                            style={{
                              color: '#6b7280',
                              fontSize: 10,
                            }}
                          >
                            ID {assetId || '-'}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
        )}
      </Box>
    </Box>
  );
}
